import { useEffect, useState } from "react";
import axios from "axios";
import { Crown, CircleDollarSign, Search, BadgeCheck, Loader2, Download, Trash2, ShieldX, Users } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dataFormatata = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "short", year: "numeric" });
};

// Tab „Useri": statistici, căutare, export CSV, segmentare, acordare/retragere
// Premium și ștergere cont (doar Fondator).
export function UsersPanel() {
  const [date, setDate] = useState(null);
  const [eroare, setEroare] = useState("");
  const [cautare, setCautare] = useState("");
  const [segment, setSegment] = useState("toti");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/users`, { withCredentials: true })
      .then((r) => setDate(r.data))
      .catch(() => setEroare("Nu am putut încărca lista de utilizatori."));
  }, []);

  const togglePremium = async (u) => {
    setBusyId(u.userId);
    try {
      if (u.isPremium) {
        await axios.post(`${API}/admin/users/${u.userId}/revoke-premium`, {}, { withCredentials: true });
        toast.success(`Premium eliminat pentru ${u.email}`);
        setDate((d) => ({
          ...d,
          premium: d.premium - 1,
          free: d.free + 1,
          users: d.users.map((x) => (x.userId === u.userId ? { ...x, isPremium: false, plan: "free", grantedByAdmin: false } : x)),
        }));
      } else {
        const r = await axios.post(`${API}/admin/users/${u.userId}/grant-premium`, {}, { withCredentials: true });
        toast.success(`Premium acordat pentru ${u.email}`);
        setDate((d) => ({
          ...d,
          premium: d.premium + 1,
          free: d.free - 1,
          users: d.users.map((x) =>
            x.userId === u.userId ? { ...x, isPremium: true, plan: "premium", premiumUntil: r.data.premiumUntil, grantedByAdmin: true } : x
          ),
        }));
      }
    } catch {
      toast.error("Acțiunea a eșuat. Încearcă din nou.");
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (u) => {
    setBusyId(u.userId);
    try {
      await axios.delete(`${API}/admin/users/${u.userId}`, { withCredentials: true });
      toast.success(`Cont șters: ${u.email}`);
      setDate((d) => ({
        ...d,
        total: d.total - 1,
        premium: u.isPremium ? d.premium - 1 : d.premium,
        free: u.isPremium ? d.free : d.free - 1,
        users: d.users.filter((x) => x.userId !== u.userId),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ștergerea a eșuat.");
    } finally {
      setBusyId(null);
    }
  };

  const exportCSV = () => {
    const linii = [["Email", "Nume", "Plan", "Premium", "Cont din"]];
    date.users.forEach((u) => {
      linii.push([u.email, u.name || "", u.isPremium ? "premium" : "free", u.isPremium ? "da" : "nu", dataFormatata(u.createdAt)]);
    });
    const csv = linii.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "chit-utilizatori.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (eroare) return <p className="text-center text-rose-500 py-10">{eroare}</p>;
  if (!date) return <Loader2 className="h-6 w-6 animate-spin text-emerald-600 mx-auto mt-10" />;

  const useriFiltrati = date.users.filter((u) => {
    if (segment === "trial_neconvertit" && !u.trialExpiredUnconverted) return false;
    if (segment === "inactivi_30" && !u.inactive30d) return false;
    const q = cautare.trim().toLowerCase();
    if (!q) return true;
    return u.email.toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2.5">
        <div data-testid="stat-total-users" className="rounded-2xl bg-white border border-slate-100 shadow-sm p-3.5 text-center">
          <Users className="h-4 w-4 text-slate-400 mx-auto mb-1" />
          <p className="font-heading text-xl font-black text-slate-900">{date.total}</p>
          <p className="text-[11px] text-slate-400">total</p>
        </div>
        <div data-testid="stat-premium-users" className="rounded-2xl bg-white border border-slate-100 shadow-sm p-3.5 text-center">
          <Crown className="h-4 w-4 text-emerald-500 mx-auto mb-1" />
          <p className="font-heading text-xl font-black text-emerald-600">{date.premium}</p>
          <p className="text-[11px] text-slate-400">premium</p>
        </div>
        <div data-testid="stat-free-users" className="rounded-2xl bg-white border border-slate-100 shadow-sm p-3.5 text-center">
          <CircleDollarSign className="h-4 w-4 text-slate-400 mx-auto mb-1" />
          <p className="font-heading text-xl font-black text-slate-700">{date.free}</p>
          <p className="text-[11px] text-slate-400">free</p>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            data-testid="admin-search-input"
            value={cautare}
            onChange={(e) => setCautare(e.target.value)}
            placeholder="Caută email sau nume..."
            className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          />
        </div>
        <button
          data-testid="export-users-csv-btn"
          onClick={exportCSV}
          className="h-11 w-11 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
          title="Exportă CSV"
        >
          <Download className="h-4 w-4" />
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { id: "toti", label: "Toți" },
          { id: "trial_neconvertit", label: "Trial expirat, neconvertit" },
          { id: "inactivi_30", label: "Inactivi 30+ zile" },
        ].map((s) => (
          <button
            key={s.id}
            data-testid={`segment-${s.id}`}
            onClick={() => setSegment(s.id)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              segment === s.id ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {useriFiltrati.length === 0 && <p className="text-center text-slate-400 text-sm py-8">Niciun utilizator găsit.</p>}
        {useriFiltrati.map((u) => (
          <div
            key={u.email}
            data-testid={`admin-user-row-${u.email}`}
            className="rounded-2xl bg-white border border-slate-100 shadow-sm p-3.5 flex items-center justify-between gap-3"
          >
            <div className="min-w-0">
              <p className="font-semibold text-sm text-slate-900 truncate">{u.name || "—"}</p>
              <p className="text-xs text-slate-500 truncate">{u.email}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Cont din {dataFormatata(u.createdAt)}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <div className="flex items-center gap-1">
                {u.isFounder ? (
                  <span className="text-[11px] font-semibold text-amber-600 flex items-center gap-1">
                    <BadgeCheck className="h-3 w-3" /> Fondator
                  </span>
                ) : u.isPremium ? (
                  <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1">
                    <Crown className="h-3 w-3" /> Premium{u.grantedByAdmin ? " (acordat)" : ""}
                  </span>
                ) : (
                  <span className="text-[11px] font-semibold text-slate-400">Free</span>
                )}
              </div>
              {u.hasSubscription && (
                <span className="text-[10px] text-slate-400">{u.billingInterval === "year" ? "abonament anual" : "abonament lunar"}</span>
              )}
              {!u.emailVerified && <span className="text-[10px] text-rose-400">email neverificat</span>}
              {!u.isFounder && (
                <div className="flex items-center gap-1.5 mt-1">
                  {(!u.isPremium || u.grantedByAdmin) && (
                    <button
                      data-testid={`admin-toggle-premium-${u.email}`}
                      onClick={() => togglePremium(u)}
                      disabled={busyId === u.userId}
                      title={u.isPremium ? "Elimină Premium" : "Acordă Premium fără plată"}
                      className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 active:scale-95 transition-all disabled:opacity-40"
                    >
                      {busyId === u.userId ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : u.isPremium ? (
                        <ShieldX className="h-3.5 w-3.5 text-amber-600" strokeWidth={2.5} />
                      ) : (
                        <Crown className="h-3.5 w-3.5" strokeWidth={2.5} />
                      )}
                    </button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        data-testid={`admin-delete-user-${u.email}`}
                        disabled={busyId === u.userId}
                        title="Șterge cont"
                        className="h-8 w-8 flex items-center justify-center rounded-lg border border-rose-200 text-rose-500 hover:bg-rose-50 active:scale-95 transition-all disabled:opacity-40"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="admin-delete-confirm-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Ștergi contul „{u.name || u.email}&rdquo;?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Contul <b>{u.email}</b> va fi șters definitiv. Grupurile/cheltuielile existente nu se șterg.
                          {u.hasSubscription ? " Abonamentul Stripe activ va fi anulat automat." : ""} Acțiunea nu poate fi anulată.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="admin-delete-cancel-btn">Anulează</AlertDialogCancel>
                        <AlertDialogAction
                          data-testid="admin-delete-confirm-btn"
                          onClick={() => deleteUser(u)}
                          className="bg-rose-500 hover:bg-rose-600"
                        >
                          Șterge definitiv
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
